import { ArrowRight, ImageIcon, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getFeatured } from "../lib/api";
import { publicEventStyle, safeAssetUrl } from "../lib/personalization";
import type { MediaItem } from "../lib/types";
import { useConfig } from "../lib/useConfig";
import { useEventPersonalization } from "../lib/useEventPersonalization";

export function MemoryHubPage() {
  const params = useParams();
  const config = useConfig();
  const clientSlug = params.clientSlug || config?.clientSlug;
  const eventSlug = params.eventSlug || config?.eventSlug;
  const eventDetails = useEventPersonalization(clientSlug, eventSlug);
  const [media, setMedia] = useState<MediaItem[]>([]);

  useEffect(() => {
    if (!clientSlug || !eventSlug) return;
    void getFeatured(clientSlug, eventSlug).then((result) => setMedia(result.media));
  }, [clientSlug, eventSlug]);

  if (!clientSlug || !eventSlug) return null;
  const heroImage = safeAssetUrl(eventDetails?.optionalHeroImage);
  const logo = safeAssetUrl(eventDetails?.optionalLogo);

  return (
    <main className="px-4 py-12 sm:px-6 lg:px-8" style={publicEventStyle(eventDetails)}>
      <section className="event-masthead mx-auto max-w-6xl rounded-lg p-6 text-white shadow-luxe sm:p-8 lg:p-10">
        <div className="grid gap-8 lg:grid-cols-[1.05fr_.95fr] lg:items-end">
          <div>
            {logo && <img src={logo} alt={`${eventDetails?.clientName || "Client"} logo`} className="mb-6 max-h-16 max-w-48 object-contain" />}
            <p className="text-xs font-extrabold uppercase tracking-[0.24em]" style={{ color: "var(--event-accent)" }}>
              {eventDetails?.eventType || "Event"} Memory Hub
            </p>
            <h1 className="mt-3 text-balance font-display text-4xl font-bold sm:text-5xl">
              {eventDetails?.memoryHubTitle || "Memory Hub"}
            </h1>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-white/72">
              {eventDetails?.memoryHubIntroText || "Upload, view, and revisit approved memories from this special event."}
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <Link
                to={`/upload/${clientSlug}/${eventSlug}`}
                className="focus-ring event-button inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-extrabold"
              >
                Upload Memories <ArrowRight className="size-4" />
              </Link>
              <Link
                to={`/gallery/${clientSlug}/${eventSlug}`}
                className="focus-ring event-button-secondary inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-extrabold"
              >
                {eventDetails?.galleryButtonLabel || "View Memory Gallery"} <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
          {heroImage && (
            <div className="event-panel overflow-hidden rounded-lg border border-white/15 bg-white/[0.06]">
              <img src={heroImage} alt={eventDetails?.eventName || "Event"} className="aspect-[4/3] h-full w-full object-cover" />
            </div>
          )}
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {media.slice(0, 6).map((item, index) => (
            <article
              key={item.id}
              className={`gallery-card overflow-hidden rounded-lg border border-ktd-gold/25 bg-white/[0.05] ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
            >
              <div className={index === 0 ? "aspect-[16/10]" : "aspect-square"}>
                {item.url ? (
                  item.fileType === "photo" ? (
                    <img src={item.url} alt={item.caption || item.fileName} loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <video src={item.url} controls preload="metadata" className="h-full w-full object-cover" />
                  )
                ) : (
                  <div className="media-frame grid h-full place-items-center">
                    <Sparkles className="size-10 text-ktd-softGold" />
                  </div>
                )}
              </div>
              {item.caption && <p className="border-t border-ktd-gold/15 p-4 text-sm font-semibold leading-6 text-white/72">{item.caption}</p>}
            </article>
          ))}
        </div>
        {!media.length && (
          <div className="mt-8 rounded-lg border border-ktd-gold/20 bg-white/[0.05] p-10 text-center">
            <ImageIcon className="mx-auto size-12 text-ktd-softGold" />
            <h2 className="mt-5 font-display text-3xl font-bold">Featured memories will appear here.</h2>
            <p className="mt-3 text-white/62">{eventDetails?.emptyGalleryMessage || "Photos and videos will appear here once they are uploaded and approved."}</p>
          </div>
        )}
      </section>
    </main>
  );
}
